import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItemButton,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as RemoveIcon,
  Save as SaveIcon,
  FolderCopy as CopyTemplateIcon,
  Settings as ManageIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { formatModuleLabel } from '../../utils/moduleLabel';
import {
  formatUserCreationTemplateTitle,
  normalizeTemplateShortName,
} from '../../utils/userCreationTemplate';

function rolesOf(g) {
  return g.roles || g.Roles || [];
}

function modulesForGroup(g) {
  const mods = new Set();
  for (const r of rolesOf(g)) {
    const perms = r.permissions || r.Permissions || [];
    for (const p of perms) {
      if (p.module) mods.add(p.module);
    }
  }
  return mods;
}

function groupTouchesModules(g, selectedModules) {
  if (!selectedModules.size) return true;
  const gm = modulesForGroup(g);
  for (const m of selectedModules) {
    if (gm.has(m)) return true;
  }
  return false;
}

/** Keep existing order, then add template ids not already present. */
function mergeGroupIds(existing, templateIds) {
  const seen = new Set(existing);
  const out = [...existing];
  for (const id of templateIds) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/**
 * Two-column picker: assigned groups (left) vs catalog filtered by module checkboxes (right).
 * Templates: built-in + saved custom (users_modify).
 */
export default function UserGroupAssignmentPicker({
  groups,
  value,
  onChange,
  disabled,
  canManageTemplates,
  hideTemplateToolbar,
}) {
  const [builtins, setBuiltins] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [selectedModules, setSelectedModules] = useState(() => new Set());
  const [templateChoice, setTemplateChoice] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [manageOpen, setManageOpen] = useState(false);
  /** When non-null, dialog asks Replace vs Append before applying/loading a template. */
  const [applyMergeIntent, setApplyMergeIntent] = useState(null); // 'apply' | 'load'

  const loadTemplates = useCallback(async () => {
    try {
      const r = await api.get('/users/group-assignment-templates');
      setBuiltins(r.data?.data?.builtins || []);
      setCustoms(r.data?.data?.customs || []);
    } catch {
      setBuiltins([]);
      setCustoms([]);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const allTemplates = useMemo(() => [...builtins, ...customs], [builtins, customs]);

  const catalogModules = useMemo(() => {
    const s = new Set();
    for (const g of groups) {
      modulesForGroup(g).forEach((m) => s.add(m));
    }
    return [...s].sort();
  }, [groups]);

  const assignedSet = useMemo(() => new Set(value), [value]);

  const assignedList = useMemo(() => {
    return value.map((id) => groups.find((g) => g.id === id)).filter(Boolean);
  }, [value, groups]);

  const availableList = useMemo(() => {
    return groups.filter((g) => !assignedSet.has(g.id) && groupTouchesModules(g, selectedModules));
  }, [groups, assignedSet, selectedModules]);

  const toggleModule = (mod) => {
    setSelectedModules((prev) => {
      const next = new Set(prev);
      if (next.has(mod)) next.delete(mod);
      else next.add(mod);
      return next;
    });
  };

  const clearModuleFilters = () => setSelectedModules(new Set());

  const addGroup = (id) => {
    if (disabled || assignedSet.has(id)) return;
    onChange([...value, id]);
  };

  const removeGroup = (id) => {
    if (disabled) return;
    onChange(value.filter((x) => x !== id));
  };

  const selectedTemplate = allTemplates.find((t) => t.id === templateChoice);

  const runTemplateOntoSelection = useCallback(
    (tpl, mode, meta = {}) => {
      const templateIds = [...(tpl.group_ids || [])];
      if (tpl.missing_stems?.length) {
        toast.warn(
          `Some seed groups are missing in this environment: ${tpl.missing_stems.join(', ')}. Using resolved groups only.`
        );
      }
      const title = formatUserCreationTemplateTitle(tpl.name);
      if (mode === 'replace') {
        onChange(templateIds);
      } else {
        onChange(mergeGroupIds(value, templateIds));
      }

      if (meta.intent === 'load-empty') {
        toast.success(`Loaded “${title}” — adjust groups, then Save user or Save as new template.`);
        return;
      }
      if (meta.intent === 'load') {
        toast.success(
          mode === 'replace'
            ? `Selection replaced with “${title}”. Adjust groups as needed.`
            : `Groups from “${title}” added to your selection. Adjust as needed.`
        );
        return;
      }
      if (mode === 'append') {
        toast.success(`Appended groups from template: ${title}`);
        return;
      }
      toast.success(meta.firstApply ? `Applied template: ${title}` : `Replaced selection with template: ${title}`);
    },
    [onChange, value]
  );

  const applySelectedTemplate = () => {
    const tpl = selectedTemplate;
    if (!tpl) {
      toast.info('Choose a template first.');
      return;
    }
    if (value.length > 0) {
      setApplyMergeIntent('apply');
      return;
    }
    runTemplateOntoSelection(tpl, 'replace', { firstApply: true });
  };

  const closeApplyMergeDialog = () => setApplyMergeIntent(null);

  const confirmApplyMerge = (mode) => {
    const tpl = selectedTemplate;
    const intent = applyMergeIntent;
    closeApplyMergeDialog();
    if (!tpl) return;
    runTemplateOntoSelection(tpl, mode, intent === 'load' ? { intent: 'load' } : {});
  };

  const saveCurrentAsTemplate = async () => {
    const nm = normalizeTemplateShortName(saveName);
    if (!nm) {
      toast.error('Enter a short name for this template.');
      return;
    }
    try {
      await api.post('/users/group-assignment-templates', {
        name: nm,
        description: '',
        group_ids: value,
      });
      toast.success('Template saved.');
      setSaveOpen(false);
      setSaveName('');
      loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save template');
    }
  };

  const copyTemplateToPicker = () => {
    const tpl = selectedTemplate;
    if (!tpl) {
      toast.info('Choose a template to copy from.');
      return;
    }
    if (value.length > 0) {
      setApplyMergeIntent('load');
      return;
    }
    runTemplateOntoSelection(tpl, 'replace', { intent: 'load-empty' });
  };

  const deleteCustomTemplate = async (id, name) => {
    if (!window.confirm(`Delete saved template “${formatUserCreationTemplateTitle(name)}”?`)) return;
    try {
      await api.delete(`/users/group-assignment-templates/${id}`);
      toast.success('Template deleted.');
      if (templateChoice === id) setTemplateChoice('');
      loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <Box>
      {!hideTemplateToolbar && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Pick groups by module, or apply a user creation template (shown as User Creation - …). Built-ins match
          standard seed groups (e.g. Group - Recruiters).
        </Typography>
      )}
      {hideTemplateToolbar && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Pick groups by module for this template definition.
        </Typography>
      )}

      {!hideTemplateToolbar && (
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.secondary.main, 0.14)
                : alpha(theme.palette.secondary.main, 0.07),
          })}
        >
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel id="ugp-template-label">User creation template</InputLabel>
            <Select
              labelId="ugp-template-label"
              label="User creation template"
              value={templateChoice}
              onChange={(e) => setTemplateChoice(e.target.value)}
              disabled={disabled}
            >
              <MenuItem value="">
                <em>None selected</em>
              </MenuItem>
              {builtins.length > 0 && (
                <MenuItem disabled sx={{ opacity: 1, fontWeight: 700, fontSize: '0.75rem' }}>
                  Built-in
                </MenuItem>
              )}
              {builtins.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {formatUserCreationTemplateTitle(t.name)}
                  {t.missing_stems?.length ? ` (partial)` : ''}
                </MenuItem>
              ))}
              {customs.length > 0 && (
                <MenuItem disabled sx={{ opacity: 1, fontWeight: 700, fontSize: '0.75rem', mt: 0.5 }}>
                  Custom
                </MenuItem>
              )}
              {customs.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {formatUserCreationTemplateTitle(t.name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" disabled={disabled || !templateChoice} onClick={applySelectedTemplate}>
            Apply template
          </Button>
          <Tooltip title="Loads template groups. If you already have groups selected, choose append or replace.">
            <span>
              <Button
                variant="text"
                size="small"
                startIcon={<CopyTemplateIcon />}
                disabled={disabled || !templateChoice}
                onClick={copyTemplateToPicker}
              >
                Load into picker
              </Button>
            </span>
          </Tooltip>
          {canManageTemplates && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SaveIcon />}
                disabled={disabled}
                onClick={() => setSaveOpen(true)}
              >
                Save as template…
              </Button>
              <Button
                variant="text"
                size="small"
                startIcon={<ManageIcon />}
                onClick={() => setManageOpen(true)}
              >
                Manage saved…
              </Button>
            </>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={(theme) => ({
            border: 1,
            borderRadius: 2,
            p: 1.5,
            minHeight: 280,
            borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.4 : 0.28),
            bgcolor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.16)
                : alpha(theme.palette.primary.main, 0.09),
            boxShadow: theme.palette.mode === 'light' ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
          })}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Assigned groups ({value.length})
          </Typography>
          <List dense disablePadding sx={{ maxHeight: 240, overflow: 'auto' }}>
            {assignedList.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No groups yet — add from the right, or apply a template.
              </Typography>
            ) : (
              assignedList.map((g) => (
                <ListItem
                  key={g.id}
                  dense
                  sx={{ borderRadius: 1 }}
                  secondaryAction={
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small" disabled={disabled} onClick={() => removeGroup(g.id)}>
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  }
                >
                  <ListItemText primary={g.name} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))
            )}
          </List>
        </Box>

        <Box
          sx={(theme) => ({
            border: 1,
            borderRadius: 2,
            p: 1.5,
            minHeight: 280,
            borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.42 : 0.3),
            bgcolor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.info.main, 0.15)
                : alpha(theme.palette.info.main, 0.09),
            boxShadow: theme.palette.mode === 'light' ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
          })}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Add groups (catalog)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Filter by permission module (optional). No boxes checked = show all unassigned groups.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, alignItems: 'center' }}>
            {catalogModules.map((m) => (
              <Chip
                key={m}
                label={formatModuleLabel(m)}
                size="small"
                onClick={() => !disabled && toggleModule(m)}
                color={selectedModules.has(m) ? 'primary' : 'default'}
                variant={selectedModules.has(m) ? 'filled' : 'outlined'}
                disabled={disabled}
              />
            ))}
            {catalogModules.length > 0 && (
              <Button size="small" onClick={clearModuleFilters} disabled={disabled || selectedModules.size === 0}>
                Clear filters
              </Button>
            )}
          </Box>
          <Divider sx={{ my: 1 }} />
          <List dense disablePadding sx={{ maxHeight: 200, overflow: 'auto' }}>
            {availableList.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No matching groups to add (all assigned or filtered out).
              </Typography>
            ) : (
              availableList.map((g) => (
                <ListItemButton
                  key={g.id}
                  dense
                  sx={{ borderRadius: 1 }}
                  disabled={disabled}
                  onClick={() => addGroup(g.id)}
                >
                  <ListItemText
                    primary={g.name}
                    secondary={[...modulesForGroup(g)].sort().map(formatModuleLabel).join(' · ') || '—'}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  <AddIcon sx={{ ml: 1, color: 'action.active', fontSize: 20 }} />
                </ListItemButton>
              ))
            )}
          </List>
        </Box>
      </Box>

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Save as user creation template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Short name"
            fullWidth
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            helperText={`Shown as ${formatUserCreationTemplateTitle(normalizeTemplateShortName(saveName) || '…')}. Saves ${value.length} group(s). Manage all templates under User Settings → User Creation Template.`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveCurrentAsTemplate}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(applyMergeIntent)} onClose={closeApplyMergeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {applyMergeIntent === 'load' ? 'Load template into picker?' : 'Apply template on existing groups?'}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            You already have {value.length} group(s) assigned.{' '}
            <strong>Replace all</strong> clears the list and keeps only the groups from this template.{' '}
            <strong>Append groups</strong> keeps your current assignment and adds any groups from the template that are
            not already selected (duplicates are skipped).
          </Typography>
          {selectedTemplate && (
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Template: {formatUserCreationTemplateTitle(selectedTemplate.name)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button onClick={closeApplyMergeDialog}>Cancel</Button>
          <Button variant="contained" onClick={() => confirmApplyMerge('append')}>
            Append groups
          </Button>
          <Button variant="outlined" color="warning" onClick={() => confirmApplyMerge('replace')}>
            Replace all
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manageOpen} onClose={() => setManageOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Custom user creation templates</DialogTitle>
        <DialogContent dividers>
          {customs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No custom templates yet. Use “Save as template…” on the user form, or User Settings → User Creation
              Template.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell align="right">Groups</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {customs.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{formatUserCreationTemplateTitle(t.name)}</TableCell>
                    <TableCell align="right">{(t.group_ids || []).length}</TableCell>
                    <TableCell align="right">
                      <Button size="small" color="error" onClick={() => deleteCustomTemplate(t.id, t.name)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
